import upperFirst from "lodash-es/upperFirst";

// Note (Evan, 2024-01-17): This is a translation of the get_valid_filename
// util in Django -- it removes all whitespace (replacing with underscores) and
// any characters that aren't an alphanumeric, underscore, dash, or period.
export const getValidFilename = (value: string) => {
  value = value.trim().replace(/\s+/g, "_");
  return value.replace(/[^\w.-]/g, "");
};

// https://gist.github.com/mathewbyrne/1280286
export function slugify(text: string) {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/[^\w-]+/g, "") // Remove all non-word chars
    .replace(/--+/g, "-") // Replace multiple - with single -
    .replace(/^-+/, "") // Trim - from start of text
    .replace(/-+$/, ""); // Trim - from end of text
}

// Note (Evan, 2024-02-13): This checks whether a metafield value (from Shopify)
// contains HTML. To quote Matt, "What is especially tricky here is that for
// multi_line_text_field metafields, a newline in liquid will be \n where in the
// API response it will be <br>. Because of this, we need to make sure we replace the <br>
// in the API value before determining if it contains HTML.
export function metafieldContainsHtml(metafieldValue: string) {
  return Boolean(
    metafieldValue.replace(/<br>/g, "\n").match(/<("[^"]*"|'[^']*'|[^"'>])*>/g),
  );
}

export function capitalizeFirstLetter(inputString: string): string {
  return upperFirst(inputString);
}

/**
 * Get url without query params.
 *
 * @author Kurt 2024-10-14
 */
export const getUrlWithoutQueryParams = (url: string) => {
  return url.split("?")[0];
};

/**
 * Converts PascalCase strings to readable format with spaces.
 * E.g., "LandingPage1" -> "Landing Page 1"
 *
 * @param pascalCaseString - The PascalCase string to convert
 * @returns A readable string with spaces between words and numbers
 */
export const convertPascalCaseToReadable = (
  pascalCaseString: string,
): string => {
  return pascalCaseString
    .replace(/([\da-z])([A-Z])/g, "$1 $2") // insert space before capital letters
    .replace(/([A-Za-z])(\d+)/g, "$1 $2") // insert space before trailing numbers
    .trim();
};

// Formats a string in the cat -n format with line numbers starting at 1 and 6 spaces per line number
export function formatWithCatN({
  content,
  startLineNumber = 1,
}: {
  content: string;
  startLineNumber?: number;
}): string {
  return content
    .split("\n")
    .map((line, idx) => {
      const lineNumber = String(idx + startLineNumber).padStart(6, " ");
      return `${lineNumber}${line}`;
    })
    .join("\n");
}

/**
 * Escapes special regex characters in a string for use in RegExp constructor
 */
export function escapeRegExp(string: string): string {
  return string.replace(/[$()*+.?[\\\]^{|}]/g, "\\$&");
}

const SKILL_NAME_ACRONYMS = new Set([
  "cro",
  "seo",
  "aeo",
  "geo",
  "llm",
  "ai",
  "ui",
  "ux",
  "ml",
  "qa",
  "kpi",
  "roi",
  "api",
  "cli",
  "sdk",
  "url",
  "id",
]);

// Small words stay lowercase unless they're the first token of the title.
const SKILL_NAME_SMALL_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "in",
  "of",
  "on",
  "or",
  "the",
  "to",
  "vs",
  "with",
]);

/**
 * Skill names are authored as kebab-case slugs in SKILL.md frontmatter
 * ("site-management", "founder-review"). Anywhere we render the name to the
 * user — skills app cards, the chat tool feed, the provisioning overlay — we
 * run it through this so they see "Site Management", "Founder Review", etc.
 * instead of the raw slug.
 *
 * Names that already look human-authored (any whitespace or any uppercase
 * letter) are returned untouched, so a skill can opt out by writing
 * `name: My Custom Skill` in frontmatter.
 */
export function prettifySkillName(name: string): string {
  if (name.trim() === "") {
    return name;
  }
  if (/\s/.test(name) || /[A-Z]/.test(name)) {
    return name;
  }
  const words = name.split(/[_-]/).filter(Boolean);
  return words
    .map((word, index) => {
      if (SKILL_NAME_ACRONYMS.has(word)) {
        return word.toUpperCase();
      }
      if (index > 0 && SKILL_NAME_SMALL_WORDS.has(word)) {
        return word;
      }
      return upperFirst(word);
    })
    .join(" ");
}

/**
 * Safely decodes a URI component, returning the original value if decoding fails.
 * Returns null if the input is null or undefined, preserving empty strings.
 */
export function decodeURIComponentSafe(
  value: string | null | undefined,
): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
